import React, { useState, useEffect, useMemo } from "react";
import { Modal, Form, Button, Row, Col, Alert, Spinner, InputGroup } from "react-bootstrap";
import { GetAllProducts } from "../Services/ProductService";
import { GetAllProductAttributesWithValues } from "../Services/AttributeService";
import { SearchUserByPhone, RegisterUser, updateUserDetails } from "../Services/UserService";
import { AddUserAddress, GetUserAddresses, GetAllCities } from "../Services/AddressService";
import { CreateOrder } from "../Services/OrderService";
import { GetAllCategories } from "../Services/CategoryService";

export default function AddSelfOrderModal({ show, handleClose, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  // Customer Form State
  const [phoneQuery, setPhoneQuery] = useState("");
  const [foundUser, setFoundUser] = useState(null);
  const [customerDetails, setCustomerDetails] = useState({
    name: "",
    email: "",
    address1: "",
    address2: "",
    zipPostalCode: "",
    city: "New Delhi", // default city
  });

  // Products, Categories & Attributes Lists
  const [categoriesList, setCategoriesList] = useState([]);
  const [productsList, setProductsList] = useState([]);
  const [attributesList, setAttributesList] = useState([]);

  // Saved addresses & City autocomplete states
  const [userAddresses, setUserAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState("");
  const [selectedCityId, setSelectedCityId] = useState(null);
  const [selectedStateId, setSelectedStateId] = useState(null);
  const [selectedCountryId, setSelectedCountryId] = useState(null);
  const [stateName, setStateName] = useState("");
  const [countryName, setCountryName] = useState("India");

  const [citySearchTerm, setCitySearchTerm] = useState("");
  const [cities, setCities] = useState([]);
  const [selectedCity, setSelectedCity] = useState(null);

  // Selected Order Items State (for user-like ordering)
  const [selectedWheatId, setSelectedWheatId] = useState("");
  const [wheatWeight, setWheatWeight] = useState(10); // default 10 Kg
  const [selectedGrains, setSelectedGrains] = useState({}); // { [prodId]: weight }
  const [selectedPulses, setSelectedPulses] = useState({}); // { [prodId]: weight }
  const [selectedGrindingValId, setSelectedGrindingValId] = useState("");
  const [selectedPackagingValId, setSelectedPackagingValId] = useState("");

  useEffect(() => {
    if (show) {
      // Reset Modal State
      setErrorMsg("");
      setSuccessMsg("");
      setFoundUser(null);
      setPhoneQuery("");
      setUserAddresses([]);
      setSelectedAddressId("");
      setSelectedCityId(null);
      setSelectedStateId(null);
      setSelectedCountryId(null);
      setStateName("");
      setCountryName("India");
      setCitySearchTerm("");
      setCities([]);
      setSelectedCity(null);
      setCustomerDetails({
        name: "",
        email: "",
        address1: "",
        address2: "",
        zipPostalCode: "",
        city: "",
      });
      setSelectedWheatId("");
      setWheatWeight(10);
      setSelectedGrains({});
      setSelectedPulses({});
      setSelectedGrindingValId("");
      setSelectedPackagingValId("");
      loadDropdowns();
    }
  }, [show]);

  const loadDropdowns = async () => {
    try {
      const userRole = localStorage.getItem("userRole");
      const vendorId = userRole === "vendor" ? localStorage.getItem("userId") : null;
      const params = { isCustomProduct: false };
      if (vendorId) {
        params.vendorId = vendorId;
      }
      const pRes = await GetAllProducts(params);
      if (pRes.status && pRes.data) {
        setProductsList(pRes.data);
      }
      const cRes = await GetAllCategories({ categoryNames: ["Wheat", "Grains", "Pulses"] });
      if (cRes.status && cRes.data) {
        setCategoriesList(cRes.data);
      }
    } catch (err) {
      console.error("Error loading dropdown data:", err);
    }
  };

  // Load product-specific attributes when selectedWheatId changes
  useEffect(() => {
    if (selectedWheatId) {
      setSelectedGrindingValId("");
      setSelectedPackagingValId("");
      loadProductAttributes(selectedWheatId);
    } else {
      setAttributesList([]);
      setSelectedGrindingValId("");
      setSelectedPackagingValId("");
    }
  }, [selectedWheatId]);

  const loadProductAttributes = async (productId) => {
    try {
      const res = await GetAllProductAttributesWithValues({
        productIds: [parseInt(productId, 10)],
        attributeIds: [5, 6] // 5: Grinding Texture, 6: Packaging
      });
      if (res.status && res.data) {
        setAttributesList(res.data);
      }
    } catch (err) {
      console.error("Error loading product-specific attributes:", err);
    }
  };

  // Auto-select default options when attributesList loads
  useEffect(() => {
    if (attributesList.length > 0) {
      // Auto-select default grinding texture
      const grindingAttr = attributesList.find(a => a.name.toLowerCase() === "grinding texture");
      if (grindingAttr) {
        const vals = grindingAttr.values || grindingAttr.productAttributeValues || [];
        if (vals.length > 0 && !selectedGrindingValId) {
          const normalGrinding = vals.find(v => v.name.toLowerCase().includes("normal") || v.name.toLowerCase().includes("medium")) || vals[0];
          setSelectedGrindingValId(normalGrinding.id.toString());
        }
      }

      // Auto-select default packaging
      const packagingAttr = attributesList.find(a => a.name.toLowerCase() === "packaging");
      if (packagingAttr) {
        const vals = packagingAttr.values || packagingAttr.productAttributeValues || [];
        if (vals.length > 0 && !selectedPackagingValId) {
          const defaultPack = vals.find(v => v.name.toLowerCase().includes("none") || v.name.toLowerCase().includes("standard")) || vals[0];
          setSelectedPackagingValId(defaultPack.id.toString());
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attributesList]);

  // Derived Category Lists (using loose/number coercion comparison)
  const wheatCat = categoriesList.find(c => c.name.toLowerCase() === "wheat");
  const wheatProducts = productsList.filter(p => wheatCat && Number(p.categoryId) === Number(wheatCat.id));

  const grainsCat = categoriesList.find(c => c.name.toLowerCase() === "grains");
  const grainsProducts = productsList.filter(p => grainsCat && Number(p.categoryId) === Number(grainsCat.id));

  const pulsesCat = categoriesList.find(c => c.name.toLowerCase() === "pulses");
  const pulsesProducts = productsList.filter(p => pulsesCat && Number(p.categoryId) === Number(pulsesCat.id));

  // Live Calculations
  const calculations = useMemo(() => {
    let totalWeight = 0;
    let totalPrice = 0;
    const itemsList = [];

    // 1. Wheat
    const wheatProduct = productsList.find(p => Number(p.id) === Number(selectedWheatId));
    if (wheatProduct) {
      const basePrice = wheatProduct.price || 0;
      const weight = parseFloat(wheatWeight) || 0;
      const itemPrice = basePrice * weight;
      totalWeight += weight;
      totalPrice += itemPrice;
      itemsList.push({
        name: `${wheatProduct.name} (Wheat)`,
        weight,
        unitPrice: basePrice,
        totalPrice: itemPrice
      });
    }

    // 2. Grains
    Object.keys(selectedGrains).forEach(grainId => {
      const grainProduct = productsList.find(p => Number(p.id) === Number(grainId));
      const weight = parseFloat(selectedGrains[grainId]) || 0;
      if (grainProduct && weight > 0) {
        const basePrice = grainProduct.price || 0;
        const itemPrice = basePrice * weight;
        totalWeight += weight;
        totalPrice += itemPrice;
        itemsList.push({
          name: `${grainProduct.name} (Grain)`,
          weight,
          unitPrice: basePrice,
          totalPrice: itemPrice
        });
      }
    });

    // 3. Pulses
    Object.keys(selectedPulses).forEach(pulseId => {
      const pulseProduct = productsList.find(p => Number(p.id) === Number(pulseId));
      const weight = parseFloat(selectedPulses[pulseId]) || 0;
      if (pulseProduct && weight > 0) {
        const basePrice = pulseProduct.price || 0;
        const itemPrice = basePrice * weight;
        totalWeight += weight;
        totalPrice += itemPrice;
        itemsList.push({
          name: `${pulseProduct.name} (Pulse)`,
          weight,
          unitPrice: basePrice,
          totalPrice: itemPrice
        });
      }
    });

    // 4. Grinding Texture Fee
    const grindingAttr = attributesList.find(a => a.name.toLowerCase() === "grinding texture");
    let grindingName = "";
    let grindingIncreasePrice = 0;
    let grindingFee = 0;
    if (grindingAttr && selectedGrindingValId) {
      const vals = grindingAttr.values || grindingAttr.productAttributeValues || [];
      const val = vals.find(v => Number(v.id) === Number(selectedGrindingValId));
      if (val) {
        grindingName = val.name;
        // Fallback to 5.0 Rs/Kg if DB price is 0 but user selected a customized grinding option
        let dbPrice = parseFloat(val.increasePrice || val.priceAdjustment || 0);
        if (dbPrice === 0 && !val.name.toLowerCase().includes("normal") && !val.name.toLowerCase().includes("standard")) {
          dbPrice = 5.0; // fallback grinding texture charge
        }
        grindingIncreasePrice = dbPrice;
        grindingFee = grindingIncreasePrice * totalWeight;
        totalPrice += grindingFee;
      }
    }

    // 5. Packaging Fee
    const packagingAttr = attributesList.find(a => a.name.toLowerCase() === "packaging");
    let packagingName = "";
    let packagingIncreasePrice = 0;
    let packagingFee = 0;
    if (packagingAttr && selectedPackagingValId) {
      const vals = packagingAttr.values || packagingAttr.productAttributeValues || [];
      const val = vals.find(v => Number(v.id) === Number(selectedPackagingValId));
      if (val) {
        packagingName = val.name;
        packagingIncreasePrice = parseFloat(val.increasePrice || val.priceAdjustment || 0);
        packagingFee = packagingIncreasePrice * totalWeight;
        totalPrice += packagingFee;
      }
    }

    return {
      totalWeight,
      totalPrice,
      itemsList,
      grindingAttr,
      grindingName,
      grindingIncreasePrice,
      grindingFee,
      packagingAttr,
      packagingName,
      packagingIncreasePrice,
      packagingFee
    };
  }, [selectedWheatId, wheatWeight, selectedGrains, selectedPulses, selectedGrindingValId, selectedPackagingValId, productsList, attributesList]);

  const handleSearchCustomer = async () => {
    if (!phoneQuery.trim()) {
      setErrorMsg("Please enter a phone number.");
      return;
    }
    setErrorMsg("");
    setLoading(true);
    setFoundUser(null);

    // Format phone query
    let formattedPhone = phoneQuery.trim();
    if (!formattedPhone.startsWith("+91")) {
      if (formattedPhone.length === 10) {
        formattedPhone = "+91" + formattedPhone;
      } else {
        setErrorMsg("Phone number must contain exactly 10 digits.");
        setLoading(false);
        return;
      }
    }

    try {
      const res = await SearchUserByPhone(formattedPhone);
      if (res.status && res.data && res.data.length > 0) {
        const user = res.data[0];
        setFoundUser(user);

        // Load customer addresses if found
        let addresses = [];
        let firstAddr = null;
        try {
          const addrRes = await GetUserAddresses(user.id);
          if (addrRes.status && addrRes.data && addrRes.data.length > 0) {
            addresses = addrRes.data;
            firstAddr = addrRes.data.find(a => a.isDefault) || addrRes.data[0];
          }
        } catch (addrErr) {
          console.error("Failed to load user addresses:", addrErr);
        }

        setUserAddresses(addresses);
        if (firstAddr) {
          setSelectedAddressId(firstAddr.id || "");
          const cName = firstAddr.city?.cityName || firstAddr.cityName || firstAddr.city || "";
          const sName = firstAddr.city?.state?.stateName || firstAddr.stateName || firstAddr.state || "";
          const coName = firstAddr.city?.state?.country?.countryName || firstAddr.countryName || firstAddr.country || "India";
          
          setSelectedCityId(firstAddr.cityId || firstAddr.city?.id || null);
          setSelectedStateId(firstAddr.stateId || firstAddr.city?.stateId || null);
          setSelectedCountryId(firstAddr.countryId || firstAddr.city?.state?.countryId || null);
          setStateName(sName);
          setCountryName(coName);

          setCustomerDetails({
            name: user.name || "",
            email: user.email || "",
            address1: firstAddr.address1 || "",
            address2: firstAddr.address2 || "",
            zipPostalCode: firstAddr.zipPostalCode || "",
            city: cName,
          });
        } else {
          setSelectedAddressId("new");
          setSelectedCityId(null);
          setSelectedStateId(null);
          setSelectedCountryId(null);
          setStateName("");
          setCountryName("India");
          setCustomerDetails({
            name: user.name || "",
            email: user.email || "",
            address1: "",
            address2: "",
            zipPostalCode: "",
            city: "",
          });
        }
      } else {
        setFoundUser(null);
        setErrorMsg("Customer not found. Please fill details to register them.");
        setCustomerDetails(prev => ({
          ...prev,
          name: "",
          email: "",
        }));
      }
    } catch (err) {
      setErrorMsg("Error searching for customer.");
    } finally {
      setLoading(false);
    }
  };

  const handleAddressSelect = (addrId) => {
    setSelectedAddressId(addrId);
    if (addrId === "new") {
      setSelectedCityId(null);
      setSelectedStateId(null);
      setSelectedCountryId(null);
      setStateName("");
      setCountryName("India");
      setCitySearchTerm("");
      setCustomerDetails(prev => ({
        ...prev,
        address1: "",
        address2: "",
        zipPostalCode: "",
        city: "",
      }));
    } else {
      const selected = userAddresses.find(a => String(a.id) === String(addrId));
      if (selected) {
        const cName = selected.city?.cityName || selected.cityName || selected.city || "";
        const sName = selected.city?.state?.stateName || selected.stateName || selected.state || "";
        const coName = selected.city?.state?.country?.countryName || selected.countryName || selected.country || "India";

        setSelectedCityId(selected.cityId || selected.city?.id || null);
        setSelectedStateId(selected.stateId || selected.city?.stateId || null);
        setSelectedCountryId(selected.countryId || selected.city?.state?.countryId || null);
        setStateName(sName);
        setCountryName(coName);

        setCustomerDetails(prev => ({
          ...prev,
          address1: selected.address1 || "",
          address2: selected.address2 || "",
          zipPostalCode: selected.zipPostalCode || "",
          city: cName,
        }));
      }
    }
  };

  // Fetch cities
  const fetchCities = async (term = "") => {
    try {
      const response = await GetAllCities({ city: term });
      if (response.status) {
        setCities(response.data || []);
      }
    } catch (error) {
      console.error("Error fetching cities:", error);
    }
  };

  // Debounced city search
  useEffect(() => {
    if (selectedCity) return;
    if (!citySearchTerm) {
      setCities([]);
      return;
    }
    const timeout = setTimeout(() => fetchCities(citySearchTerm), 400);
    return () => clearTimeout(timeout);
  }, [citySearchTerm, selectedCity]);

  // Synchronize citySearchTerm when customerDetails.city is updated by searching customer or selecting a saved address
  useEffect(() => {
    if (customerDetails.city) {
      setCitySearchTerm(customerDetails.city);
      setSelectedCity(customerDetails.city);
    } else {
      setCitySearchTerm("");
      setSelectedCity(null);
    }
  }, [customerDetails.city]);

  const handleCitySelect = (city) => {
    setSelectedCity(city);
    setCitySearchTerm(city.name);
    setCities([]);
    
    setSelectedCityId(city.id);
    setSelectedStateId(city.stateId);
    setSelectedCountryId(city.countryId);
    setStateName(city.stateName);
    setCountryName(city.countryName || "India");

    setCustomerDetails(prev => ({
      ...prev,
      city: city.name
    }));
  };

  const handleSubmitOrder = async () => {
    if (!phoneQuery.trim()) {
      setErrorMsg("Please enter a customer mobile number.");
      return;
    }

    let phoneNo = phoneQuery.trim();
    if (!phoneNo.startsWith("+91")) {
      if (phoneNo.length === 10) {
        phoneNo = "+91" + phoneNo;
      } else {
        setErrorMsg("Phone number must contain exactly 10 digits.");
        return;
      }
    }

    if (!customerDetails.name || !customerDetails.address1 || !customerDetails.zipPostalCode) {
      setErrorMsg("Please fill in Customer Name, Address, and Zip Code.");
      return;
    }

    if (!selectedWheatId) {
      setErrorMsg("Please select a Wheat product.");
      return;
    }

    const selectedWheatProduct = productsList.find(p => Number(p.id) === Number(selectedWheatId));
    if (!selectedWheatProduct) {
      setErrorMsg("Wheat product not found.");
      return;
    }

    setLoading(true);
    setErrorMsg("");

    try {
      let finalUserId = foundUser?.id;

      // 1. Register User if not found
      if (!finalUserId) {
        const regPayload = {
          name: customerDetails.name,
          email: customerDetails.email || `${phoneNo.replace("+", "")}@refinedgrains.com`,
          Password: btoa("123456"), // default password
          phone: phoneNo,
          dateOfBirth: new Date().toISOString(),
          longitude: "77.1025", // Default Delhi coordinates
          latitude: "28.7041",
          role: "customer"
        };

        const regRes = await RegisterUser(regPayload);
        if (!regRes.status || !regRes.data) {
          throw new Error(regRes.message || "Failed to register customer user account.");
        }
        finalUserId = regRes.data.id;
      } else {
        // If user already exists, update email if admin specified one and it's different/new
        const enteredEmail = customerDetails.email?.trim() || "";
        const existingEmail = foundUser.email?.trim() || "";
        const dummyEmail = `${phoneNo.replace("+", "")}@refinedgrains.com`;

        if (enteredEmail && enteredEmail !== existingEmail && enteredEmail !== dummyEmail) {
          try {
            const updatePayload = {
              name: foundUser.name || customerDetails.name,
              email: enteredEmail,
              phone: phoneNo,
              dateOfBirth: foundUser.dateOfBirth || new Date().toISOString(),
              longitude: foundUser.longitude || "77.1025",
              latitude: foundUser.latitude || "28.7041"
            };
            await updateUserDetails(finalUserId, updatePayload);
          } catch (updateErr) {
            console.error("Failed to update customer email:", updateErr);
          }
        }
      }

      // 2. Reuse Saved Address or Create New Address
      let addressId = null;
      if (selectedAddressId && selectedAddressId !== "new") {
        addressId = parseInt(selectedAddressId, 10);
      } else {
        const addressPayload = {
          userId: finalUserId,
          firstName: customerDetails.name,
          lastName: "",
          email: customerDetails.email || foundUser?.email || "customer@refinedgrains.com",
          countryId: selectedCountryId || 1,
          stateId: selectedStateId || 1,
          cityId: selectedCityId || 1,
          address1: customerDetails.address1,
          address2: customerDetails.address2 || "",
          zipPostalCode: customerDetails.zipPostalCode,
          phoneNumber: phoneNo,
          active: true,
          isDefault: true,
        };

        const addrRes = await AddUserAddress(addressPayload);
        if (!addrRes.status || !addrRes.data) {
          throw new Error(addrRes.message || "Failed to save customer shipping address.");
        }
        addressId = addrRes.data.id;
      }

      // 3. Generate Catalog XML matching customer customise-wheat schema
      const mainProductXml = `<Product><Item Id="${selectedWheatProduct.id}" Name="${selectedWheatProduct.name}" Price="${selectedWheatProduct.price || 0}" TotalPrice="${(selectedWheatProduct.price * wheatWeight).toFixed(2)}" Weight="${wheatWeight} Kg" /></Product>`;

      let subProductsItemsXml = "";
      Object.keys(selectedGrains).forEach(grainId => {
        const p = productsList.find(prod => Number(prod.id) === Number(grainId));
        const w = parseFloat(selectedGrains[grainId]) || 0;
        if (p && w > 0) {
          subProductsItemsXml += `<Item Id="${p.id}" Name="${p.name}" Price="${p.price || 0}" TotalPrice="${(p.price * w).toFixed(2)}" Weight="${w} Kg" />`;
        }
      });
      Object.keys(selectedPulses).forEach(pulseId => {
        const p = productsList.find(prod => Number(prod.id) === Number(pulseId));
        const w = parseFloat(selectedPulses[pulseId]) || 0;
        if (p && w > 0) {
          subProductsItemsXml += `<Item Id="${p.id}" Name="${p.name}" Price="${p.price || 0}" TotalPrice="${(p.price * w).toFixed(2)}" Weight="${w} Kg" />`;
        }
      });
      const subProductsXml = `<SubProducts>${subProductsItemsXml}</SubProducts>`;

      let attributesItemsXml = "";
      if (calculations.grindingAttr && selectedGrindingValId) {
        const valId = parseInt(selectedGrindingValId, 10);
        const vals = calculations.grindingAttr.values || calculations.grindingAttr.productAttributeValues || [];
        const val = vals.find(v => Number(v.id) === Number(valId));
        if (val) {
          attributesItemsXml += `<Item Id="${calculations.grindingAttr.id}" Name="${calculations.grindingAttr.name}" ValueId="${val.id}" Value="${val.name}" IncreasePrice="${calculations.grindingIncreasePrice}" TotalIncreasePrice="${calculations.grindingFee.toFixed(2)}" />`;
        }
      }
      if (calculations.packagingAttr && selectedPackagingValId) {
        const valId = parseInt(selectedPackagingValId, 10);
        const vals = calculations.packagingAttr.values || calculations.packagingAttr.productAttributeValues || [];
        const val = vals.find(v => Number(v.id) === Number(valId));
        if (val) {
          attributesItemsXml += `<Item Id="${calculations.packagingAttr.id}" Name="${calculations.packagingAttr.name}" ValueId="${val.id}" Value="${val.name}" IncreasePrice="${calculations.packagingIncreasePrice}" TotalIncreasePrice="${calculations.packagingFee.toFixed(2)}" />`;
        }
      }
      const attributesXml = `<Attributes>${attributesItemsXml}</Attributes>`;

      const catalogXml = `<Catalog>${mainProductXml}${subProductsXml}${attributesXml}</Catalog>`;

      // 4. Create Order Payload
      const orderPayload = {
        userId: finalUserId,
        billingAddressId: addressId,
        shippingAddressId: addressId,
        orderSubtotal: calculations.totalPrice,
        orderSubTotalDiscount: 0,
        orderDiscount: 0,
        orderTotal: calculations.totalPrice,
        totalWeight: calculations.totalWeight,
        couponCode: "",
        paymentMethodSystemName: "COD",
        orderItems: [
          {
            productId: selectedWheatProduct.id,
            quantity: 1, // 1 custom flour mix bag
            weight: calculations.totalWeight,
            totalWeight: calculations.totalWeight,
            unitPrice: calculations.totalPrice,
            discountUnitPrice: 0,
            totalPrice: calculations.totalPrice,
            attributesXml: catalogXml,
            discountId: 0,
            discount: 0,
          }
        ]
      };

      const orderRes = await CreateOrder(orderPayload);
      if (orderRes.status) {
        setSuccessMsg("Order created successfully!");
        setTimeout(() => {
          onSuccess();
          handleClose();
        }, 1500);
      } else {
        throw new Error(orderRes.message || "Failed to create the order.");
      }
    } catch (err) {
      setErrorMsg(err.message || "Error submitting manual order.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} dialogClassName="self-order-modal-dialog" centered scrollable className="self-order-modal">
      <style>{`
        .self-order-modal-dialog {
          max-width: 96% !important;
          width: 96% !important;
          margin: 1.5rem auto;
        }
        .self-order-modal .modal-content {
          border-radius: 16px;
          border: none;
          box-shadow: 0 10px 40px rgba(0,0,0,0.12);
        }
        .self-order-modal .modal-header {
          background-color: #ffffff;
          border-bottom: 1px solid rgba(0,0,0,0.06);
          padding: 1.5rem 2rem;
        }
        .self-order-modal .modal-body {
          background-color: #fcfbfa;
          padding: 2rem;
        }
        .self-order-card {
          background: #ffffff;
          border-radius: 14px;
          border: 1px solid rgba(107, 79, 50, 0.08);
          box-shadow: 0 4px 20px rgba(0,0,0,0.02);
          margin-bottom: 1.5rem;
          padding: 1.5rem 1.8rem;
          transition: all 0.25s ease;
        }
        .self-order-card:hover {
          box-shadow: 0 6px 25px rgba(107, 79, 50, 0.05);
        }
        .self-order-card-title {
          font-size: 1.15rem;
          font-weight: 700;
          color: #6b4f32;
          margin-bottom: 1.25rem;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .self-order-card-title::before {
          content: "";
          display: inline-block;
          width: 4px;
          height: 18px;
          background-color: #6b4f32;
          border-radius: 2px;
        }
        .texture-option-card {
          border: 2px solid #eae6e2;
          border-radius: 10px;
          padding: 12px 14px;
          cursor: pointer;
          text-align: center;
          background: #ffffff;
          transition: all 0.2s ease;
          position: relative;
        }
        .texture-option-card:hover {
          border-color: #cbbba9;
          background-color: #fbfaf9;
        }
        .texture-option-card.selected {
          border-color: #6b4f32;
          background-color: #f7f3ef;
          color: #6b4f32;
        }
        .texture-option-card.selected::after {
          content: "✓";
          position: absolute;
          top: 4px;
          right: 8px;
          font-weight: bold;
          font-size: 0.8rem;
          color: #6b4f32;
        }
        .addon-item-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 8px 12px;
          border-radius: 8px;
          border: 1px solid transparent;
          transition: all 0.2s ease;
        }
        .addon-item-row:hover {
          background-color: #fcfbfa;
          border-color: rgba(107, 79, 50, 0.05);
        }
        .price-badge {
          background: rgba(107, 79, 50, 0.07);
          color: #6b4f32;
          font-weight: 600;
          padding: 3px 9px;
          border-radius: 20px;
          font-size: 0.75rem;
          margin-left: 8px;
          display: inline-block;
        }
        .summary-card {
          background: #ffffff;
          border-radius: 14px;
          border: 1px solid rgba(107, 79, 50, 0.12);
          border-top: 4px solid #6b4f32 !important;
          box-shadow: 0 6px 30px rgba(107, 79, 50, 0.06);
          padding: 1.5rem 1.8rem;
        }
        .place-order-btn {
          background-color: #6b4f32;
          border: none;
          border-radius: 8px;
          padding: 12px;
          font-weight: 700;
          font-size: 1.05rem;
          letter-spacing: 0.5px;
          transition: all 0.2s ease;
        }
        .place-order-btn:hover:not(:disabled) {
          background-color: #533d26;
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(107, 79, 50, 0.2);
        }
        .place-order-btn:active:not(:disabled) {
          transform: translateY(0);
        }
        .custom-section-hdr {
          color: #6b4f32;
          font-size: 0.95rem;
          font-weight: 700;
          border-bottom: 2px solid rgba(107, 79, 50, 0.1);
          padding-bottom: 6px;
        }
      `}</style>

      <Modal.Header closeButton className="border-0 pb-0 bg-white">
        <Modal.Title className="fw-bold" style={{ color: "#6b4f32" }}>
          + Create Call/Self Order
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="px-4 py-3 bg-light" style={{ maxHeight: "80vh" }}>
        {errorMsg && <Alert variant="danger">{errorMsg}</Alert>}
        {successMsg && <Alert variant="success">{successMsg}</Alert>}

        <Row>
          {/* Left Column: Form Configuration */}
          <Col lg={8}>
            {/* Customer Search & Details Card */}
            <div className="self-order-card">
              <div className="self-order-card-title">1. Customer Details</div>

              <Row className="align-items-end mb-3">
                <Col md={8}>
                  <Form.Group>
                    <Form.Label className="fw-semibold text-muted small uppercase mb-1">Customer Mobile Number</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter 10-digit mobile number"
                      value={phoneQuery}
                      onChange={(e) => setPhoneQuery(e.target.value)}
                      className="py-2"
                      style={{ borderRadius: "8px" }}
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Button
                    variant="outline-primary"
                    className="w-100 fw-bold py-2"
                    style={{ borderColor: "#6b4f32", color: "#6b4f32", height: "43px", borderRadius: "8px" }}
                    onClick={handleSearchCustomer}
                    disabled={loading}
                  >
                    {loading ? <Spinner size="sm" /> : "SEARCH CUSTOMER"}
                  </Button>
                </Col>
              </Row>

              {foundUser && (
                <Alert variant="success" className="py-2 mb-3 d-flex align-items-center gap-2">
                  <span className="fs-5">✓</span>
                  <div>
                    Registered customer found: <strong>{foundUser.name}</strong> ({foundUser.email})
                  </div>
                </Alert>
              )}

              {foundUser && userAddresses.length > 0 && (
                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold text-muted small uppercase mb-1">Select Saved Address</Form.Label>
                  <Form.Select 
                    value={selectedAddressId} 
                    onChange={(e) => handleAddressSelect(e.target.value)}
                    className="py-2"
                    style={{ borderRadius: "8px" }}
                  >
                    {userAddresses.map(addr => (
                      <option key={addr.id} value={addr.id}>
                        {addr.address1}, {addr.city?.cityName || addr.cityName || addr.city || ""}, {addr.zipPostalCode} {addr.isDefault ? "(Default)" : ""}
                      </option>
                    ))}
                    <option value="new">+ Add New Address</option>
                  </Form.Select>
                </Form.Group>
              )}

              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="fw-semibold text-muted small uppercase mb-1">Customer Name</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter full name"
                      value={customerDetails.name}
                      onChange={(e) => setCustomerDetails({ ...customerDetails, name: e.target.value })}
                      required
                      disabled={!!foundUser}
                      className="py-2"
                      style={{ borderRadius: "8px" }}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="fw-semibold text-muted small uppercase mb-1">Email Address (Optional)</Form.Label>
                    <Form.Control
                      type="email"
                      placeholder="Enter email address"
                      value={customerDetails.email}
                      onChange={(e) => setCustomerDetails({ ...customerDetails, email: e.target.value })}
                      className="py-2"
                      style={{ borderRadius: "8px" }}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold text-muted small uppercase mb-1">Shipping Address Line 1</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Flat / House no, Building, Street details"
                  value={customerDetails.address1}
                  onChange={(e) => setCustomerDetails({ ...customerDetails, address1: e.target.value })}
                  required
                  className="py-2"
                  style={{ borderRadius: "8px" }}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold text-muted small uppercase mb-1">Shipping Address Line 2 (Optional)</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Landmark, Area"
                  value={customerDetails.address2}
                  onChange={(e) => setCustomerDetails({ ...customerDetails, address2: e.target.value })}
                  className="py-2"
                  style={{ borderRadius: "8px" }}
                />
              </Form.Group>

              <Row>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label className="fw-semibold text-muted small uppercase mb-1">Zip / Postal Code</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="e.g. 110001"
                      value={customerDetails.zipPostalCode}
                      onChange={(e) => setCustomerDetails({ ...customerDetails, zipPostalCode: e.target.value })}
                      required
                      className="py-2"
                      style={{ borderRadius: "8px" }}
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="position-relative">
                    <Form.Label className="fw-semibold text-muted small uppercase mb-1">City</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Search city"
                      value={citySearchTerm}
                      onChange={(e) => {
                        setCitySearchTerm(e.target.value);
                        setSelectedCity(null);
                        setCustomerDetails({ ...customerDetails, city: e.target.value });
                      }}
                      required
                      className="py-2"
                      style={{ borderRadius: "8px" }}
                    />
                    {cities.length > 0 && (
                      <ul
                        className="list-group position-absolute w-100"
                        style={{ top: "100%", zIndex: 2000, maxHeight: "150px", overflowY: "auto", boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }}
                      >
                        {cities.map((city) => (
                          <li
                            key={city.id}
                            className="list-group-item list-group-item-action text-start"
                            onClick={() => handleCitySelect(city)}
                            style={{ cursor: "pointer" }}
                          >
                            {city.name} ({city.stateName})
                          </li>
                        ))}
                      </ul>
                    )}
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label className="fw-semibold text-muted small uppercase mb-1">State</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="State"
                      value={stateName}
                      readOnly
                      className="py-2 bg-light"
                      style={{ borderRadius: "8px" }}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </div>

            {/* Flour Customization Card */}
            <div className="self-order-card">
              <div className="self-order-card-title">2. Flour Mix Customization</div>

              {/* Wheat Product & Weight Selection */}
              <Row className="mb-4">
                <Col md={7}>
                  <Form.Group>
                    <Form.Label className="fw-semibold text-muted small uppercase mb-1">Select Wheat Base Product</Form.Label>
                    <Form.Select
                      value={selectedWheatId}
                      onChange={(e) => setSelectedWheatId(e.target.value)}
                      required
                      className="py-2"
                      style={{ borderRadius: "8px" }}
                    >
                      <option value="">-- Choose Wheat Base --</option>
                      {wheatProducts.map(p => (
                        <option key={p.id} value={p.id}>
                          {p.name} (₹{p.price}/Kg)
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={5}>
                  <Form.Group>
                    <Form.Label className="fw-semibold text-muted small uppercase mb-1">Wheat Weight (Kg)</Form.Label>
                    <InputGroup className="input-group-custom">
                      <Form.Control
                        type="number"
                        min="1"
                        step="0.5"
                        value={wheatWeight}
                        onChange={(e) => setWheatWeight(parseFloat(e.target.value) || 0)}
                        required
                        className="py-2"
                        style={{ borderRadius: "8px 0 0 8px" }}
                      />
                      <InputGroup.Text style={{ borderRadius: "0 8px 8px 0" }}>Kg</InputGroup.Text>
                    </InputGroup>
                  </Form.Group>
                </Col>
              </Row>

              {/* Grinding Texture Selection (Dropdown) */}
              {calculations.grindingAttr && (
                <div className="mb-4">
                  <Form.Group>
                    <Form.Label className="fw-semibold text-muted small uppercase mb-2">{calculations.grindingAttr.name}</Form.Label>
                    <Form.Select
                      value={selectedGrindingValId}
                      onChange={(e) => setSelectedGrindingValId(e.target.value)}
                      className="py-2"
                      style={{ borderRadius: "8px" }}
                    >
                      <option value="">-- Choose Grinding Texture --</option>
                      {(calculations.grindingAttr.values || calculations.grindingAttr.productAttributeValues || []).map(val => {
                        let ip = parseFloat(val.increasePrice || val.priceAdjustment || 0);
                        if (ip === 0 && !val.name.toLowerCase().includes("normal") && !val.name.toLowerCase().includes("standard")) {
                          ip = 5.0;
                        }
                        return (
                          <option key={val.id} value={val.id}>
                            {val.name} {ip > 0 ? `(+₹${ip}/Kg)` : "(Free)"}
                          </option>
                        );
                      })}
                    </Form.Select>
                  </Form.Group>
                </div>
              )}

              {/* Packaging Selection */}
              {calculations.packagingAttr && (
                <div className="mb-4">
                  <Form.Group>
                    <Form.Label className="fw-semibold text-muted small uppercase mb-2">{calculations.packagingAttr.name}</Form.Label>
                    <Form.Select
                      value={selectedPackagingValId}
                      onChange={(e) => setSelectedPackagingValId(e.target.value)}
                      className="py-2"
                      style={{ borderRadius: "8px" }}
                    >
                      <option value="">-- Choose Packaging --</option>
                      {(calculations.packagingAttr.values || calculations.packagingAttr.productAttributeValues || []).map(val => {
                        const ip = val.increasePrice || val.priceAdjustment || 0;
                        return (
                          <option key={val.id} value={val.id}>
                            {val.name} {ip > 0 ? `(+₹${ip}/Kg)` : "(Free)"}
                          </option>
                        );
                      })}
                    </Form.Select>
                  </Form.Group>
                </div>
              )}

              <hr />

              {/* Add-ons: Grains & Pulses */}
              <Row>
                {/* Grains Column */}
                <Col md={6} className="border-end">
                  <div className="custom-section-hdr mb-3">Add-on Grains</div>
                  <div style={{ maxHeight: "300px", overflowY: "auto", paddingRight: "10px" }}>
                    {grainsProducts.length === 0 ? (
                      <p className="text-muted small">No grains products available.</p>
                    ) : (
                      grainsProducts.map(p => {
                        const weight = selectedGrains[p.id] || "";
                        return (
                          <div key={p.id} className="addon-item-row mb-2 d-flex align-items-center justify-content-between bg-white p-2" style={{ borderRadius: "8px", border: "1px solid #eae6e2" }}>
                            <span className="fw-semibold text-dark" style={{ fontSize: "0.9rem" }}>
                              {p.name}
                              <span className="price-badge">₹{p.price}/Kg</span>
                            </span>
                            <div style={{ width: "110px" }}>
                              <InputGroup size="sm" className="input-group-custom">
                                <Form.Control
                                  type="number"
                                  min="0"
                                  step="0.1"
                                  value={weight}
                                  placeholder="0.0"
                                  onChange={(e) => {
                                    const val = parseFloat(e.target.value) || 0;
                                    setSelectedGrains(prev => {
                                      const updated = { ...prev };
                                      if (val > 0) {
                                        updated[p.id] = val;
                                      } else {
                                        delete updated[p.id];
                                      }
                                      return updated;
                                    });
                                  }}
                                  style={{ borderRadius: "6px 0 0 6px", textAlign: "center", fontWeight: "bold" }}
                                />
                                <InputGroup.Text style={{ borderRadius: "0 6px 6px 0", fontSize: "0.75rem", padding: "4px 8px" }}>Kg</InputGroup.Text>
                              </InputGroup>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </Col>

                {/* Pulses Column */}
                <Col md={6}>
                  <div className="custom-section-hdr mb-3">Add-on Pulses</div>
                  <div style={{ maxHeight: "300px", overflowY: "auto", paddingRight: "10px" }}>
                    {pulsesProducts.length === 0 ? (
                      <p className="text-muted small">No pulses products available.</p>
                    ) : (
                      pulsesProducts.map(p => {
                        const weight = selectedPulses[p.id] || "";
                        return (
                          <div key={p.id} className="addon-item-row mb-2 d-flex align-items-center justify-content-between bg-white p-2" style={{ borderRadius: "8px", border: "1px solid #eae6e2" }}>
                            <span className="fw-semibold text-dark" style={{ fontSize: "0.9rem" }}>
                              {p.name}
                              <span className="price-badge">₹{p.price}/Kg</span>
                            </span>
                            <div style={{ width: "110px" }}>
                              <InputGroup size="sm" className="input-group-custom">
                                <Form.Control
                                  type="number"
                                  min="0"
                                  step="0.1"
                                  value={weight}
                                  placeholder="0.0"
                                  onChange={(e) => {
                                    const val = parseFloat(e.target.value) || 0;
                                    setSelectedPulses(prev => {
                                      const updated = { ...prev };
                                      if (val > 0) {
                                        updated[p.id] = val;
                                      } else {
                                        delete updated[p.id];
                                      }
                                      return updated;
                                    });
                                  }}
                                  style={{ borderRadius: "6px 0 0 6px", textAlign: "center", fontWeight: "bold" }}
                                />
                                <InputGroup.Text style={{ borderRadius: "0 6px 6px 0", fontSize: "0.75rem", padding: "4px 8px" }}>Kg</InputGroup.Text>
                              </InputGroup>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </Col>
              </Row>
            </div>
          </Col>

          {/* Right Column: Order Summary */}
          <Col lg={4}>
            <div className="summary-card sticky-top" style={{ top: "10px" }}>
              <h5 className="fw-bold mb-3" style={{ color: "#6b4f32", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span>Order Summary</span>
                <span className="fs-6 text-muted">Self Order</span>
              </h5>
              <hr />

              {calculations.itemsList.length === 0 ? (
                <p className="text-muted text-center py-4">No products selected yet.</p>
              ) : (
                <>
                  <div className="mb-3" style={{ maxHeight: "250px", overflowY: "auto" }}>
                    {calculations.itemsList.map((item, idx) => (
                      <div key={idx} className="d-flex justify-content-between mb-2 small">
                        <div>
                          <strong>{item.name}</strong>
                          <div className="text-muted">{item.weight} Kg @ ₹{item.unitPrice}/Kg</div>
                        </div>
                        <div className="fw-semibold">₹{item.totalPrice.toFixed(2)}</div>
                      </div>
                    ))}
                  </div>

                  <hr />

                  {/* Attribute Adjustments */}
                  {calculations.grindingFee > 0 && (
                    <div className="d-flex justify-content-between mb-2 small text-muted">
                      <div>Grinding ({calculations.grindingName})</div>
                      <div>+₹{calculations.grindingFee.toFixed(2)}</div>
                    </div>
                  )}

                  {calculations.packagingFee > 0 && (
                    <div className="d-flex justify-content-between mb-2 small text-muted">
                      <div>Packaging ({calculations.packagingName})</div>
                      <div>+₹{calculations.packagingFee.toFixed(2)}</div>
                    </div>
                  )}

                  <hr />

                  <div className="d-flex justify-content-between mb-2 fw-semibold">
                    <div>Total Weight:</div>
                    <div>{calculations.totalWeight.toFixed(2)} Kg</div>
                  </div>

                  <div className="d-flex justify-content-between mb-4 fs-5 fw-bold text-success">
                    <div>Total Price:</div>
                    <div>₹{calculations.totalPrice.toFixed(2)}</div>
                  </div>
                </>
              )}

              <Button
                className="w-100 fw-bold py-2 mt-2 place-order-btn"
                onClick={handleSubmitOrder}
                disabled={loading || calculations.itemsList.length === 0}
              >
                {loading ? <Spinner size="sm" /> : "PLACE SELF ORDER"}
              </Button>
            </div>
          </Col>
        </Row>
      </Modal.Body>
    </Modal>
  );
}
